import React, { useState } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Button, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  Alert,
  CircularProgress,
  MenuItem,
  Switch,
  FormControlLabel,
  Tabs,
  Tab,
  Divider
} from '@mui/material';
import { 
  Add as AddIcon, 
  Edit as EditIcon, 
  Delete as DeleteIcon,
  ArrowBack as BackIcon,
  Translate as TranslateIcon,
  KeyboardArrowUp as UpIcon,
  KeyboardArrowDown as DownIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useActivities, useCreateActivity, useUpdateActivity, useDeleteActivity } from '../../hooks/useActivities';
import { supabase } from '../../lib/supabase';
import { 
  adminColors, 
  textFieldStyles, 
  selectMenuProps,
  dialogStyles, 
  tableStyles, 
  buttonStyles, 
  switchStyles,
  chipStyles 
} from './adminStyles';
import { useTranslation } from 'react-i18next';

const AdminActivities = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { data: activities, isLoading, error, refetch } = useActivities({ isPublished: null, language: 'fr' });
  const createActivity = useCreateActivity();
  const updateActivity = useUpdateActivity();
  const deleteActivity = useDeleteActivity();
  
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    type: 'conference',
    date: '',
    url: '',
    location: '',
    cover_image: '',
    image_url: [],
    is_published: false,
    translations: {
      fr: { title: '', tagline: '', description: '' },
      en: { title: '', tagline: '', description: '' }
    }
  });

  const activityTypes = [
    { value: 'conference', label: t('activities.types.conference'), color: '#0FA3A3' },
    { value: 'meetup', label: t('activities.types.meetup'), color: '#D4AF37' },
    { value: 'training', label: t('activities.types.training'), color: '#8B5CF6' },
    { value: 'publication', label: t('activities.types.publication'), color: '#EC4899' },
    { value: 'podcast', label: t('activities.types.podcast'), color: '#10B981' }
  ];

  const handleOpenCreate = () => {
    setSelectedActivity(null);
    setFormData({
      type: 'conference',
      date: '',
      url: '',
      location: '',
      cover_image: '',
      image_url: [],
      is_published: false,
      translations: {
        fr: { title: '', tagline: '', description: '' },
        en: { title: '', tagline: '', description: '' }
      }
    });
    setTabValue(0);
    setDialogOpen(true);
  };

  const handleOpenEdit = (activity) => {
    setSelectedActivity(activity);
    const frTrans = activity.translations?.find(t => t.language_code === 'fr') || {};
    const enTrans = activity.translations?.find(t => t.language_code === 'en') || {};
    
    setFormData({
      type: activity.type || 'conference',
      date: activity.date || '',
      url: activity.url || '',
      location: activity.location || '',
      cover_image: activity.cover_image || '',
      image_url: Array.isArray(activity.image_url) ? activity.image_url : (activity.image_url ? [activity.image_url] : []),
      is_published: activity.is_published || false,
      translations: {
        fr: { 
          title: frTrans.title || '', 
          tagline: frTrans.tagline || '',
          description: frTrans.description || '' 
        },
        en: { 
          title: enTrans.title || '', 
          tagline: enTrans.tagline || '',
          description: enTrans.description || '' 
        }
      }
    });
    setTabValue(0);
    setDialogOpen(true);
  };

  const handleOpenDelete = (activity) => {
    setSelectedActivity(activity);
    setDeleteDialogOpen(true);
  };

  const handleMove = async (activity, direction) => {
    const currentIndex = activities.findIndex(a => a.id === activity.id);
    const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    
    if (targetIndex < 0 || targetIndex >= activities.length) return;
    
    const targetActivity = activities[targetIndex];
    
    try {
      const tempOrder = activity.display_order || 0;
      await supabase.from('activities').update({ display_order: targetActivity.display_order || 0 }).eq('id', activity.id);
      await supabase.from('activities').update({ display_order: tempOrder }).eq('id', targetActivity.id);
      await refetch();
    } catch (err) {
      console.error('Error reordering activities:', err);
    }
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      const activityData = {
        type: formData.type,
        date: formData.date || null,
        url: formData.url || null,
        location: formData.location || null,
        cover_image: formData.cover_image || null,
        image_url: formData.image_url,
        is_published: formData.is_published
      };

      let activityId;

      if (selectedActivity) {
        await updateActivity.mutateAsync({ id: selectedActivity.id, ...activityData });
        activityId = selectedActivity.id;
        
        for (const lang of ['fr', 'en']) {
          const transData = {
            activity_id: activityId,
            language_code: lang,
            title: formData.translations[lang].title,
            tagline: formData.translations[lang].tagline,
            description: formData.translations[lang].description
          };
          
          const { data: existing } = await supabase
            .from('activities_translations')
            .select('id')
            .eq('activity_id', activityId)
            .eq('language_code', lang)
            .maybeSingle();
          
          if (existing) {
            await supabase
              .from('activities_translations')
              .update(transData)
              .eq('id', existing.id);
          } else {
            await supabase
              .from('activities_translations')
              .insert({ id: crypto.randomUUID(), ...transData });
          }
        }
      } else {
        const result = await createActivity.mutateAsync(activityData);
        activityId = result.id;
        
        for (const lang of ['fr', 'en']) {
          await supabase
            .from('activities_translations')
            .insert({
              id: crypto.randomUUID(),
              activity_id: activityId,
              language_code: lang,
              title: formData.translations[lang].title,
              tagline: formData.translations[lang].tagline,
              description: formData.translations[lang].description
            });
        }
      }
      
      await refetch();
      setDialogOpen(false);
    } catch (err) {
      console.error('Error saving activity:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteActivity.mutateAsync(selectedActivity.id);
      setDeleteDialogOpen(false);
    } catch (err) {
      console.error('Error deleting activity:', err);
    }
  };

  const updateTranslation = (lang, field, value) => {
    setFormData(prev => ({
      ...prev,
      translations: {
        ...prev.translations,
        [lang]: { ...prev.translations[lang], [field]: value }
      }
    }));
  };

  const getTypeInfo = (type) => {
    return activityTypes.find(t => t.value === type) || { label: type, color: adminColors.text.secondary };
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', bgcolor: adminColors.bg.page }}>
        <CircularProgress sx={{ color: adminColors.accent.teal }} />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: adminColors.bg.page, pt: 12, pb: 8 }}>
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton onClick={() => navigate('/admin')} sx={{ color: adminColors.text.primary }}>
              <BackIcon />
            </IconButton>
            <Typography variant="h4" sx={{ color: adminColors.text.primary, fontWeight: 700 }}>
              {t('admin.sections.activities')}
            </Typography>
          </Box>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreate} sx={buttonStyles.primary}>
            {t('admin.actions.create')}
          </Button>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 3 }}>{error.message}</Alert>}

        <TableContainer component={Paper} sx={tableStyles.container}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={tableStyles.headerCell}>{t('admin.labels.title')} (FR)</TableCell>
                <TableCell sx={tableStyles.headerCell}>{t('admin.labels.category')}</TableCell>
                <TableCell sx={tableStyles.headerCell}>{t('admin.labels.order')}</TableCell>
                <TableCell sx={tableStyles.headerCell}>{t('admin.labels.date')}</TableCell>
                <TableCell sx={tableStyles.headerCell}>{t('admin.labels.status')}</TableCell>
                <TableCell sx={tableStyles.headerCell} align="right">{t('admin.labels.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {activities?.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} sx={{ ...tableStyles.bodyCell, textAlign: 'center', py: 4, color: adminColors.text.secondary }}>
                    {t('admin.messages.noData')}
                  </TableCell>
                </TableRow>
              )}
              {activities?.map((activity) => {
                const typeInfo = getTypeInfo(activity.type);
                return (
                  <TableRow key={activity.id} sx={tableStyles.row}>
                    <TableCell sx={tableStyles.bodyCell}>{activity.title || '-'}</TableCell>
                    <TableCell sx={tableStyles.bodyCell}>
                      <Chip label={typeInfo.label} size="small" sx={chipStyles.type(typeInfo.color)} />
                    </TableCell>
                    <TableCell sx={tableStyles.bodyCell}>
                      <Stack direction="row">
                        <IconButton 
                          size="small" 
                          onClick={() => handleMove(activity, 'up')} 
                          disabled={activities.indexOf(activity) === 0}
                          sx={{ color: adminColors.accent.teal }}
                        >
                          <UpIcon fontSize="small" />
                        </IconButton>
                        <IconButton 
                          size="small" 
                          onClick={() => handleMove(activity, 'down')} 
                          disabled={activities.indexOf(activity) === activities.length - 1}
                          sx={{ color: adminColors.accent.teal }}
                        >
                          <DownIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    </TableCell>
                    <TableCell sx={{ ...tableStyles.bodyCell, color: adminColors.text.secondary }}>{activity.date || '-'}</TableCell>
                    <TableCell sx={tableStyles.bodyCell}>
                      <Chip 
                        label={activity.is_published ? t('admin.labels.published') : t('admin.labels.draft')} 
                        size="small" 
                        sx={activity.is_published ? chipStyles.published : chipStyles.draft} 
                      />
                    </TableCell>
                    <TableCell sx={tableStyles.bodyCell} align="right">
                      <IconButton onClick={() => handleOpenEdit(activity)} sx={buttonStyles.iconEdit}>
                        <EditIcon />
                      </IconButton>
                      <IconButton onClick={() => handleOpenDelete(activity)} sx={buttonStyles.iconDelete}>
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx: dialogStyles }}>
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: adminColors.text.primary, borderBottom: `1px solid ${adminColors.border.default}`, pb: 2 }}>
            <TranslateIcon sx={{ color: adminColors.accent.teal }} />
            {selectedActivity ? `${t('admin.actions.edit')} ${t('admin.sections.activities')}` : `${t('admin.actions.create')} ${t('admin.sections.activities')}`}
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            <Stack spacing={3} sx={{ mt: 1 }}>
              <TextField
                select
                label={t('admin.labels.category')}
                fullWidth
                value={formData.type}
                onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                sx={textFieldStyles}
                SelectProps={selectMenuProps}
              >
                {activityTypes.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
              
              <Divider sx={{ borderColor: adminColors.border.default }} />
              
              <Box>
                <Typography variant="subtitle2" sx={{ color: adminColors.accent.gold, mb: 2, display: 'flex', alignItems: 'center', gap: 1, fontWeight: 600 }}>
                  <TranslateIcon fontSize="small" /> {t('admin.translations.title')}
                </Typography>
                <Tabs 
                  value={tabValue} 
                  onChange={(e, v) => setTabValue(v)} 
                  sx={{ 
                    mb: 2,
                    '& .MuiTabs-indicator': { bgcolor: adminColors.accent.teal }
                  }}
                >
                  <Tab label={t('admin.translations.tabs.fr')} sx={{ color: tabValue === 0 ? adminColors.accent.teal : adminColors.text.secondary, fontWeight: tabValue === 0 ? 600 : 400 }} />
                  <Tab label={t('admin.translations.tabs.en')} sx={{ color: tabValue === 1 ? adminColors.accent.teal : adminColors.text.secondary, fontWeight: tabValue === 1 ? 600 : 400 }} />
                </Tabs>
                
                {tabValue === 0 && (
                  <Stack spacing={2}>
                    <TextField
                      label={`${t('admin.labels.title')} (FR)`}
                      fullWidth
                      value={formData.translations.fr.title}
                      onChange={(e) => updateTranslation('fr', 'title', e.target.value)}
                      sx={textFieldStyles}
                    />
                    <TextField
                      label="Accroche (FR)"
                      fullWidth
                      value={formData.translations.fr.tagline}
                      onChange={(e) => updateTranslation('fr', 'tagline', e.target.value)}
                      sx={textFieldStyles}
                    />
                    <TextField
                      label={`${t('admin.labels.description')} (FR)`}
                      fullWidth
                      multiline
                      rows={4}
                      value={formData.translations.fr.description}
                      onChange={(e) => updateTranslation('fr', 'description', e.target.value)}
                      sx={textFieldStyles}
                    />
                  </Stack>
                )}
                
                {tabValue === 1 && (
                  <Stack spacing={2}>
                    <TextField
                      label={`${t('admin.labels.title')} (EN)`}
                      fullWidth
                      value={formData.translations.en.title}
                      onChange={(e) => updateTranslation('en', 'title', e.target.value)}
                      sx={textFieldStyles}
                    />
                    <TextField
                      label="Tagline (EN)"
                      fullWidth
                      value={formData.translations.en.tagline}
                      onChange={(e) => updateTranslation('en', 'tagline', e.target.value)}
                      sx={textFieldStyles}
                    />
                    <TextField
                      label={`${t('admin.labels.description')} (EN)`}
                      fullWidth
                      multiline
                      rows={4}
                      value={formData.translations.en.description}
                      onChange={(e) => updateTranslation('en', 'description', e.target.value)}
                      sx={textFieldStyles}
                    />
                  </Stack>
                )}
              </Box>
              
              <Divider sx={{ borderColor: adminColors.border.default }} />
              
              <TextField
                label={t('admin.labels.date')}
                fullWidth
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                InputLabelProps={{ shrink: true }}
                sx={textFieldStyles}
              />
              <TextField
                label="Location"
                fullWidth
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                sx={textFieldStyles}
              />
              <TextField
                label="Cover Image URL"
                fullWidth
                value={formData.cover_image}
                onChange={(e) => setFormData({ ...formData, cover_image: e.target.value })}
                sx={textFieldStyles}
              />
              <Box>
                <Typography variant="subtitle2" sx={{ color: adminColors.text.primary, mb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  Additional Images
                  <Button 
                    size="small" 
                    startIcon={<AddIcon />} 
                    onClick={() => setFormData(prev => ({ ...prev, image_url: [...prev.image_url, ''] }))}
                    sx={{ color: adminColors.accent.teal }}
                  >
                    Add Image
                  </Button>
                </Typography>
                <Stack spacing={2}>
                  {formData.image_url.map((url, index) => (
                    <Box key={index} sx={{ display: 'flex', gap: 1 }}>
                      <TextField
                        fullWidth
                        placeholder="https://example.com/image.jpg"
                        value={url}
                        onChange={(e) => {
                          const newUrls = [...formData.image_url];
                          newUrls[index] = e.target.value;
                          setFormData({ ...formData, image_url: newUrls });
                        }}
                        sx={textFieldStyles}
                      />
                      <IconButton 
                        onClick={() => {
                          const newUrls = formData.image_url.filter((_, i) => i !== index);
                          setFormData({ ...formData, image_url: newUrls });
                        }}
                        sx={{ color: adminColors.status.error }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  ))}
                  {formData.image_url.length === 0 && (
                    <Typography variant="body2" sx={{ color: adminColors.text.secondary, fontStyle: 'italic' }}>
                      No additional images added.
                    </Typography>
                  )}
                </Stack>
              </Box>
              <TextField
                label="URL"
                fullWidth
                value={formData.url}
                onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                sx={textFieldStyles}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_published}
                    onChange={(e) => setFormData({ ...formData, is_published: e.target.checked })}
                    sx={switchStyles}
                  />
                }
                label={t('admin.labels.published')}
                sx={{ color: adminColors.text.primary }}
              />
            </Stack>
          </DialogContent>
          <DialogActions sx={{ p: 3, borderTop: `1px solid ${adminColors.border.default}` }}>
            <Button onClick={() => setDialogOpen(false)} sx={buttonStyles.secondary}>{t('admin.actions.cancel')}</Button>
            <Button onClick={handleSubmit} variant="contained" disabled={saving} sx={buttonStyles.primary}>
              {saving ? <CircularProgress size={24} sx={{ color: adminColors.bg.page }} /> : t('admin.actions.save')}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} PaperProps={{ sx: dialogStyles }}>
          <DialogTitle sx={{ color: adminColors.text.primary }}>{t('admin.messages.confirmDeleteTitle')}</DialogTitle>
          <DialogContent>
            <Typography sx={{ color: adminColors.text.secondary }}>{t('admin.messages.confirmDelete')}</Typography>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setDeleteDialogOpen(false)} sx={buttonStyles.secondary}>{t('admin.actions.cancel')}</Button>
            <Button 
              onClick={handleDelete} 
              variant="contained" 
              disabled={deleteActivity.isPending}
              sx={{ bgcolor: adminColors.status.error, '&:hover': { bgcolor: '#DC2626' } }}
            >
              {deleteActivity.isPending ? <CircularProgress size={24} /> : t('admin.actions.delete')}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};

export default AdminActivities;
